import React, { useState } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  makeStyles,
  Typography,
  CircularProgress,
  Checkbox,
  TextField,
  InputAdornment,
  Box,
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import PropTypes from 'prop-types'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import DialogWithCloseIcon from '../../components/DialogWithCloseIcon/DialogWithCloseIcon'
import getOrgPlayers from '../../Apis/player/getOrgPlayers'
import assignPlayerToSeasonTeam from '../../Apis/player/assignPlayerToSeasonTeam'
import removePlayerFromSeasonTeam from '../../Apis/player/removePlayerFromSeasonTeam'
import QueryKeys from '../../utils/constants/queryKeys'

const useStyles = makeStyles((theme) => ({
  tableRow: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4),
  },
  emptyMessage: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
  searchField: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
}))

const PlayerAssignModal = (props) => {
  const { isOpen, onClose, seasonTeamId, orgId, currentPlayers } = props
  const classes = useStyles()
  const queryClient = useQueryClient()
  const [searchQuery, setSearchQuery] = useState('')

  // Fetch all org players
  const { data: allPlayersData, isLoading } = useQuery(
    [QueryKeys.GET_ORG_PLAYERS, orgId, null, searchQuery, 1, 1000],
    () =>
      getOrgPlayers({
        orgId: orgId,
        teamId: null,
        Q: searchQuery || undefined,
        page: 1,
        perPage: 1000,
      }),
    {
      enabled: Boolean(isOpen && orgId),
    }
  )

  const assignPlayerMutation = useMutation(
    (requestData) => assignPlayerToSeasonTeam(orgId, requestData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QueryKeys.GET_SEASON_TEAM_PLAYERS])
      },
    }
  )

  const removePlayerMutation = useMutation(
    ({ orgId, seasonTeamPlayerId }) => removePlayerFromSeasonTeam(orgId, seasonTeamPlayerId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QueryKeys.GET_SEASON_TEAM_PLAYERS])
      },
    }
  )

  const getAssignedPlayer = (playerId) => {
    // The currentPlayers array contains season team player assignments
    // We need to match the player ID from the org players list
    // with the playerId field in the season team player assignments
    return currentPlayers?.find((p) =>
      p.playerId === playerId ||  // If API returns playerId separately
      p.id === playerId ||         // If id is the player ID
      p.player?.id === playerId    // If there's a nested player object
    )
  }

  const getSeasonTeamPlayerIdForDeletion = (assignedPlayer) => {
    // The deletion endpoint needs the season team player assignment ID, not the player ID
    // Try different possible field names
    return assignedPlayer?.seasonTeamPlayerId ||
           assignedPlayer?.assignmentId ||
           assignedPlayer?.id  // Fallback to id field
  }

  const isPlayerAssigned = (playerId) => {
    return !!getAssignedPlayer(playerId)
  }

  const handleSelectPlayer = async (player) => {
    const assignedPlayer = getAssignedPlayer(player.id)

    if (assignedPlayer) {
      // Player is assigned - remove them
      const seasonTeamPlayerIdToDelete = getSeasonTeamPlayerIdForDeletion(assignedPlayer)
      await removePlayerMutation.mutateAsync({
        orgId: orgId,
        seasonTeamPlayerId: seasonTeamPlayerIdToDelete
      })
    } else {
      // Player is not assigned - assign them
      const payload = {
        seasonTeamId: seasonTeamId,
        playerId: player.id,
        jerseyNumber: null,
      }
      await assignPlayerMutation.mutateAsync(payload)
    }
  }

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value)
  }

  const allPlayers = allPlayersData?.players || []

  return (
    <DialogWithCloseIcon
      open={isOpen}
      dialogWidth='600px'
      onClose={onClose}
      dialogTitle={
        <Typography component='div' variant='h6' style={{ fontWeight: 'bold' }}>
          Assign Players to Season Team
        </Typography>
      }
    >
      <Box mb={2}>
        <TextField
          className={classes.searchField}
          placeholder="Search players..."
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {isLoading ? (
        <div className={classes.loading}>
          <CircularProgress />
        </div>
      ) : allPlayers && allPlayers.length > 0 ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding='checkbox'></TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Height</TableCell>
                <TableCell>Positions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allPlayers.map((player) => {
                const isAssigned = isPlayerAssigned(player.id)
                const positions = player.personalData?.positions?.map((p) => p.name).join(', ') || '-'
                const height = player.personalData?.height ? `${player.personalData.height} cm` : '-'

                return (
                  <TableRow
                    key={player.id}
                    className={classes.tableRow}
                    onClick={() => handleSelectPlayer(player)}
                  >
                    <TableCell padding='checkbox'>
                      <Checkbox checked={isAssigned} />
                    </TableCell>
                    <TableCell>
                      {player.personalData?.firstName} {player.personalData?.lastName}
                    </TableCell>
                    <TableCell>{height}</TableCell>
                    <TableCell>{positions}</TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <div className={classes.emptyMessage}>
          <Typography>No players available to assign</Typography>
        </div>
      )}
    </DialogWithCloseIcon>
  )
}

PlayerAssignModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  seasonTeamId: PropTypes.string,
  orgId: PropTypes.string,
  currentPlayers: PropTypes.array,
}

PlayerAssignModal.defaultProps = {
  currentPlayers: [],
}

export default PlayerAssignModal
